import {
    QueryClient,
    useMutation,
    useQuery,
    useSuspenseQuery,
    type UseQueryOptions
} from '@tanstack/react-query';
import superjson from 'superjson';
import type { z } from 'zod';

import type { OperationName, Operations } from '@/server/ops/registry';
import {
    ACCESS_HEADER,
    API_BASE_PATH,
    ENCODING_HEADER,
    SUPERJSON_ENCODING
} from '@/shared/protocol';

export type { OperationName };

export type OperationInput<N extends OperationName> = z.input<
    Operations[N]['input']
>;

export type OperationOutput<N extends OperationName> = Awaited<
    ReturnType<Operations[N]['handler']>
>;

export class ApiError extends Error {
    constructor(
        message: string,
        readonly status: number,
        readonly code: string
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            retry: (failureCount, error) =>
                !(error instanceof ApiError && error.status < 500) &&
                failureCount < 2,
            refetchOnWindowFocus: false
        }
    }
});

interface ResponseBody {
    data?: unknown;
    error?: { code: string; message: string };
}

const SESSION_EXPIRED = 'Your session expired. Reload the page.';

async function post(name: string, input: unknown): Promise<unknown> {
    const response = await fetch(`${API_BASE_PATH}/${name}`, {
        method: 'POST',
        credentials: 'same-origin',
        // The API never redirects; a redirect is a proxy in the way.
        redirect: 'manual',
        headers: {
            'content-type': 'application/json',
            'x-requested-with': 'XMLHttpRequest',
            [ENCODING_HEADER]: SUPERJSON_ENCODING
        },
        body: superjson.stringify(input ?? {})
    });

    if (response.type === 'opaqueredirect') {
        throw new ApiError(SESSION_EXPIRED, 401, 'session_expired');
    }

    // A write can commit before it fails (a message saved, then the send
    // refused), so every write or danger response refetches the page data.
    const access = response.headers.get(ACCESS_HEADER);
    if (access === 'write' || access === 'danger') {
        void queryClient.invalidateQueries();
    }

    let body: ResponseBody;
    try {
        const text = await response.text();
        body =
            response.headers.get(ENCODING_HEADER) === SUPERJSON_ENCODING
                ? superjson.parse<ResponseBody>(text)
                : (JSON.parse(text) as ResponseBody);
    } catch {
        throw new ApiError(
            response.status === 401
                ? SESSION_EXPIRED
                : `Request failed (${response.status})`,
            response.status,
            'invalid_response'
        );
    }

    if (!response.ok || body.error) {
        throw new ApiError(
            body.error?.message ?? `Request failed (${response.status})`,
            response.status,
            body.error?.code ?? 'error'
        );
    }
    return body.data;
}

/**
 * Runs one app operation through the API. After a write, every query on
 * the page refetches, so lists and details show the change.
 */
export function callOperation<N extends OperationName>(
    name: N,
    ...[input]: undefined extends OperationInput<N>
        ? [input?: OperationInput<N>]
        : [input: OperationInput<N>]
): Promise<OperationOutput<N>> {
    return post(name, input) as Promise<OperationOutput<N>>;
}

export function operationQueryKey<N extends OperationName>(
    name: N,
    input?: OperationInput<N>
) {
    return ['operation', name, input ?? {}] as const;
}

/** Reads an operation's result, suspending until it loads. */
export function useOperation<N extends OperationName>(
    name: N,
    input: OperationInput<N>
): OperationOutput<N> {
    return useSuspenseQuery({
        queryKey: operationQueryKey(name, input),
        queryFn: () => post(name, input) as Promise<OperationOutput<N>>
    }).data;
}

/** Reads an operation's result without suspending. */
export function useOperationQuery<N extends OperationName>(
    name: N,
    input: OperationInput<N>,
    options: Omit<
        UseQueryOptions<OperationOutput<N>, Error>,
        'queryKey' | 'queryFn'
    > = {}
) {
    return useQuery({
        ...options,
        queryKey: operationQueryKey(name, input),
        queryFn: () => post(name, input) as Promise<OperationOutput<N>>
    });
}

/** A mutation that runs an operation. */
export function useOperationMutation<N extends OperationName>(name: N) {
    return useMutation({
        mutationFn: (input: OperationInput<N>) =>
            post(name, input) as Promise<OperationOutput<N>>
    });
}
